import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceTool,
  type EmailBulkDraftCardQuery,
  EmailBulkSendDraftState
} from '@oysterjs/ais/api/graphql';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

export type LiveBulkDraft = NonNullable<EmailBulkDraftCardQuery['emailBulkSend']>;

/** The BULK_EMAIL_DRAFT operation that makes a bulk draft; the others name one it made. */
const CREATE_OPERATION = 'CREATE';

export const isBulkEmailDraftCreate = (event: StageEvent): boolean =>
  event.toolCall?.bulkEmailDraftTool?.input?.operation === CREATE_OPERATION;

/** The bulk draft an event made or changed; undefined until CREATE answers. */
export const bulkEmailDraftSendId = (event: StageEvent): string | undefined => {
  const tool = event.toolCall?.bulkEmailDraftTool;
  return (
    tool?.output?.bulkSendId ||
    tool?.input?.add?.bulkSendId ||
    tool?.input?.remove?.bulkSendId ||
    tool?.input?.fill?.bulkSendId ||
    undefined
  );
};

const isBulkEmailDraftEvent = (event: StageEvent): boolean =>
  event.toolCall?.type === CaraWorkspaceTool.BulkEmailDraft;

/**
 * The bulk draft events of one turn that a later event of the turn shows the card for, so a
 * CREATE followed by FILL shows one card. A CREATE that failed gives way to a later CREATE that
 * worked.
 */
export const supersededBulkEmailDraftEventIds = (turnEvents: StageEvent[]): Set<string> => {
  const supersededEventIds = new Set<string>();
  const laterBulkSendIds = new Set<string>();
  let hasLaterCreate = false;
  for (const event of [...turnEvents].reverse()) {
    if (!isBulkEmailDraftEvent(event)) continue;
    const bulkSendId = bulkEmailDraftSendId(event);
    const isShownLater = bulkSendId ? laterBulkSendIds.has(bulkSendId) : hasLaterCreate;
    if (isShownLater) {
      supersededEventIds.add(event.id);
    }
    if (bulkSendId && !event.error) {
      laterBulkSendIds.add(bulkSendId);
      hasLaterCreate ||= isBulkEmailDraftCreate(event);
    }
  }
  return supersededEventIds;
};

/** Every bulk draft the chat's events name, each once. */
export const workspaceBulkSendIds = (workspace: CaraWorkspaceFieldsFragment): string[] => {
  const bulkSendIds = workspace.planStages.flatMap((stage) =>
    stage.events.flatMap(
      (event) => (isBulkEmailDraftEvent(event) && bulkEmailDraftSendId(event)) || []
    )
  );
  return [...new Set(bulkSendIds)];
};

export const isBulkDraftDrafting = (bulkDraft: LiveBulkDraft): boolean =>
  bulkDraft.recipients.some(
    (recipient) => recipient.draftState === EmailBulkSendDraftState.Drafting
  );

export type BulkEmailDraftCardState =
  | { kind: 'creating' }
  | { kind: 'notCreated' }
  | { kind: 'drafting'; finished: number; total: number }
  /** Nothing drafting now; both counts are zero before the first FILL. */
  | { kind: 'settled'; drafted: number; failed: number };

/** What the card for a bulk draft event says, from the bulk draft as last read. */
export const bulkEmailDraftCardState = (
  event: StageEvent,
  bulkDraft: LiveBulkDraft | undefined
): BulkEmailDraftCardState => {
  if (!bulkEmailDraftSendId(event)) {
    return event.error ? { kind: 'notCreated' } : { kind: 'creating' };
  }
  if (!bulkDraft) {
    return { kind: 'settled', drafted: 0, failed: 0 };
  }
  const { total, finished, failed } = bulkDraft.draftProgress;
  return isBulkDraftDrafting(bulkDraft)
    ? { kind: 'drafting', finished, total }
    : { kind: 'settled', drafted: finished - failed, failed };
};
